import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useReviseKnowledgeBaseArticleMutation,
  useSetKnowledgeBaseArticlePublicationMutation,
} from "knowledge-base/_api/knowledge-base-api";
import ArticleAttachments from "knowledge-base/articles/_views/article-attachments";
import ArticleMarkdown from "knowledge-base/articles/_views/article-markdown";
import LetterPreview from "knowledge-base/form-letters/_views/letter-preview";
import ArticleHistory from "knowledge-base/articles/_views/article-history";
import KnowledgeBaseArticle from "knowledge-base/knowledge-base-article";
import { useKnowledgeBaseLocations } from "knowledge-base/knowledge-base-locations";
import { useState } from "react";
import { useNavigate } from "react-router";

type Props = Readonly<{ article: KnowledgeBaseArticle }>;

type EditorTab = "write" | "preview" | "history";

export default function ArticleEditor({ article }: Props) {
  const navigate = useNavigate();
  // Leaving the editor returns to the article where it actually lives — Attorney Hub's library
  // stays Attorney Hub's, and a client's library keeps the owner in the query string.
  const { articlePath } = useKnowledgeBaseLocations();

  const [title, setTitle] = useState(article.title);
  const [body, setBody] = useState(article.body);
  const [subject, setSubject] = useState(article.templateSubject ?? "");
  const [note, setNote] = useState("");
  const [tab, setTab] = useState<EditorTab>("write");
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const [revise, { isLoading: isSaving }] = useReviseKnowledgeBaseArticleMutation();
  const [setPublication, { isLoading: isChangingPublication }] =
    useSetKnowledgeBaseArticlePublicationMutation();

  const isDirty =
    title !== article.title ||
    body !== article.body ||
    subject !== (article.templateSubject ?? "");

  const save = async () => {
    setFailure(undefined);
    const result = await revise({
      articleId: article.id,
      title,
      body,
      templateSubject: article.isFormLetter ? subject : undefined,
      note: note.trim() || undefined,
    });

    if ("error" in result) {
      // The server's message is written for the author: which placeholder is undeclared, why the
      // title will not do. Showing it verbatim is more use than a generic failure.
      setFailure(messageFrom(result.error) ?? "The article could not be saved.");
      return;
    }

    setNote("");
    navigate(articlePath(result.data.slug));
  };

  const changePublication = async (action: "publish" | "unpublish" | "archive" | "unarchive") => {
    setFailure(undefined);
    const result = await setPublication({ articleId: article.id, action });

    if ("error" in result) {
      setFailure(messageFrom(result.error) ?? `The article could not be ${action}ed.`);
    }
  };

  return (
    <Box component="section" sx={{ maxWidth: 900 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
        <Typography component="h1" variant="h5">
          Editing {article.title}
        </Typography>
        {article.isDraft && <Chip color="warning" label="Draft" size="small" />}
        {article.isArchived && <Chip label="Archived" size="small" variant="outlined" />}
      </Stack>

      {article.isFormLetter && (
        <Alert icon={<EmailOutlinedIcon />} severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>This letter is sent to people outside Attorney Hub</AlertTitle>
          Saving a change here changes what the next recipient reads. <strong>Preview</strong>{" "}
          renders the letter as it is currently saved, so check it after saving and use{" "}
          <strong>History</strong> if you need to go back.
        </Alert>
      )}

      {failure && (
        <Alert onClose={() => setFailure(undefined)} severity="error" sx={{ mb: 2 }}>
          {failure}
        </Alert>
      )}

      <Tabs onChange={(_event, value: EditorTab) => setTab(value)} sx={{ mb: 2 }} value={tab}>
        <Tab
          icon={<EditNoteIcon fontSize="small" />}
          iconPosition="start"
          label="Write"
          value="write"
        />
        <Tab
          icon={<VisibilityIcon fontSize="small" />}
          iconPosition="start"
          label={article.isFormLetter ? "Preview letter" : "Preview"}
          value="preview"
        />
        <Tab
          icon={<HistoryIcon fontSize="small" />}
          iconPosition="start"
          label="History"
          value="history"
        />
      </Tabs>

      {tab === "write" && (
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Title"
            onChange={(event) => setTitle(event.target.value)}
            value={title}
          />

          {article.isFormLetter && (
            <TextField
              fullWidth
              helperText="What the recipient sees in their inbox. Placeholders work here too."
              label="Subject line"
              onChange={(event) => setSubject(event.target.value)}
              value={subject}
            />
          )}

          <TextField
            fullWidth
            label="Body"
            maxRows={40}
            minRows={20}
            multiline
            onChange={(event) => setBody(event.target.value)}
            slotProps={{
              input: { sx: { fontFamily: "monospace", fontSize: "0.9rem" } },
            }}
            value={body}
          />

          <TextField
            fullWidth
            helperText="Recorded in the history, so the next person can see why this changed."
            label="What changed, and why"
            onChange={(event) => setNote(event.target.value)}
            value={note}
          />

          <Divider />

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              disabled={!isDirty || isSaving}
              onClick={save}
              startIcon={<SaveIcon />}
              variant="contained"
            >
              {isSaving ? "Saving…" : "Save"}
            </Button>
            <Button
              disabled={isSaving}
              onClick={() => navigate(articlePath(article.slug))}
            >
              Cancel
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {article.isPublished ? (
              <Button
                disabled={isChangingPublication}
                onClick={() => changePublication("unpublish")}
                startIcon={<UnpublishedOutlinedIcon />}
              >
                Unpublish
              </Button>
            ) : (
              <Button
                disabled={isChangingPublication}
                onClick={() => changePublication("publish")}
                startIcon={<PublishIcon />}
                variant="outlined"
              >
                Publish
              </Button>
            )}
            {article.isArchived ? (
              <Button
                disabled={isChangingPublication}
                onClick={() => changePublication("unarchive")}
                startIcon={<UnarchiveOutlinedIcon />}
              >
                Restore
              </Button>
            ) : (
              <Button
                color="warning"
                disabled={isChangingPublication}
                onClick={() => changePublication("archive")}
                startIcon={<ArchiveOutlinedIcon />}
              >
                Archive
              </Button>
            )}
          </Stack>

          <Divider />

          {/*
            Below the save buttons, deliberately. Attaching and removing take effect immediately
            rather than on Save, and putting them among the fields would invite an author to think
            an attachment is part of the draft they can still abandon with Cancel.
          */}
          <ArticleAttachments article={article} canEdit />
        </Stack>
      )}

      {tab === "preview" &&
        (article.isFormLetter ? (
          <LetterPreview article={article} />
        ) : (
          <Paper elevation={0} sx={{ border: 1, borderColor: "divider", p: 3 }}>
            <ArticleMarkdown>{body}</ArticleMarkdown>
          </Paper>
        ))}

      {tab === "history" && <ArticleHistory article={article} />}
    </Box>
  );
}

/**
 * Pulls the server's own explanation out of an RTK Query error.
 *
 * The knowledge base answers a rejected save with a sentence meant for the author, so it is worth
 * digging for rather than replacing with "something went wrong".
 */
function messageFrom(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return undefined;
  }

  const data = (error as { data: unknown }).data;
  if (typeof data === "string" && data.trim().length > 0) {
    return data;
  }

  if (typeof data === "object" && data !== null && "title" in data) {
    const title = (data as { title: unknown }).title;
    return typeof title === "string" ? title : undefined;
  }

  return undefined;
}
